import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Undo2, MoreHorizontal, Pencil, Ban } from "lucide-react";
import { formatApiError } from "@/lib/api";
import {
  getOrderPaymentsSummary,
  listOrderPayments,
  createOrderPayment,
  updateOrderPayment,
  voidOrderPayment,
} from "@/lib/masterOrderApi";

/* ------- money format (kept in sync with FinishedOrderBlock) --------- */
const NBSP = "\u00A0";
const fmtMoneyMinor = (minor, currency) => {
  if (minor == null || currency == null) return "—";
  const neg = minor < 0;
  const abs = Math.abs(minor);
  const major = Math.floor(abs / 100);
  const cents = String(abs % 100).padStart(2, "0");
  const majorStr = String(major).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${neg ? "−" : ""}${majorStr},${cents}${NBSP}${currency}`;
};

/* ------- human major → integer minor (safe, refuses ≤0) -------------- */
const majorInputToMinor = (raw) => {
  if (raw === "" || raw == null) return "EMPTY";
  const norm = String(raw).replace(",", ".").trim();
  if (!norm) return "EMPTY";
  const n = Number(norm);
  if (!Number.isFinite(n) || n <= 0) return "INVALID";
  return Math.round(n * 100);
};

/* ------- date/time --------------------------------------------------- */
const RU_MONTHS_GEN = [
  "января","февраля","марта","апреля","мая","июня",
  "июля","августа","сентября","октября","ноября","декабря",
];
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.getDate();
  const mon = RU_MONTHS_GEN[d.getMonth()];
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${mon} · ${hh}:${mm}`;
};
/** ISO datetime → local "YYYY-MM-DDTHH:mm" for the datetime-local input. */
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const nowLocalInput = () => isoToLocalInput(new Date().toISOString());

const METHOD_LABEL = {
  cash: "Наличные",
  bank_transfer: "Банковский перевод",
  card: "Карта",
  other: "Другое",
};
const METHODS = ["cash", "bank_transfer", "card", "other"];

/* -------- Summary strip (top of drawer) --------------------------------- */
function DrawerSummary({ summary }) {
  if (!summary) return null;
  const { currency_code, sale_total_minor, net_paid_minor, remaining_minor, overpayment_minor, is_total_reliable } = summary;
  if (!is_total_reliable) {
    return (
      <div className="rounded-lg p-3 mb-3 space-y-1.5"
           style={{ background: "#FBF3E4", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
           data-testid="mo-payments-drawer-summary">
        <Row label="Оплачено" value={fmtMoneyMinor(net_paid_minor, currency_code)} testid="mo-payments-drawer-paid" />
        <div className="text-[12px] pt-0.5" style={{ color: "#8B6F4B" }}>
          <div style={{ color: "#5B4326" }}>Итоговая стоимость ещё рассчитывается</div>
          <div>Не для всех позиций заказа готов расчёт.</div>
        </div>
      </div>
    );
  }
  const isFullyPaid = remaining_minor === 0 && (overpayment_minor || 0) === 0;
  const hasOver = (overpayment_minor || 0) > 0;
  return (
    <div className="rounded-lg p-3 mb-3 space-y-1.5"
         style={{ background: "#FBF3E4", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-payments-drawer-summary">
      <Row label="Стоимость заказа" value={fmtMoneyMinor(sale_total_minor, currency_code)} />
      <Row label="Оплачено" value={fmtMoneyMinor(net_paid_minor, currency_code)} testid="mo-payments-drawer-paid" />
      {isFullyPaid ? (
        <div className="text-[12px] pt-0.5" style={{ color: "#4A3728" }}>Оплачено полностью</div>
      ) : hasOver ? (
        <Row label="Переплата" value={fmtMoneyMinor(overpayment_minor, currency_code)} />
      ) : (
        <Row label="К доплате" value={fmtMoneyMinor(remaining_minor, currency_code)} testid="mo-payments-drawer-remaining" />
      )}
    </div>
  );
}
function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[12px]" style={{ color: "#5B4326" }}>{label}</span>
      <span className="text-[13px] tabular-nums whitespace-nowrap" style={{ color: "#2A1D14" }}
            data-testid={testid}>{value}</span>
    </div>
  );
}

/* -------- Single history row -------------------------------------------- */
function PaymentRow({ entry, currency, canWrite, onEdit, onAnnul }) {
  const isRefund = entry.type === "refund";
  const isVoided = entry.is_voided;
  const [menuOpen, setMenuOpen] = useState(false);
  const amountStr = fmtMoneyMinor(entry.amount_minor, entry.currency_code || currency);
  return (
    <div className={`rounded-lg p-3 relative ${isVoided ? "opacity-60" : ""}`}
         style={{ background: "white", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-payments-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] tabular-nums whitespace-nowrap"
                  style={{ color: isRefund ? "#7A2E00" : "#2A1D14" }}
                  data-testid="mo-payments-row-amount">
              {isRefund ? "−" : ""}{amountStr}
            </span>
            {isRefund && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full"
                    style={{ background: "#F1E1D3", color: "#7A2E00" }}
                    data-testid="mo-payments-row-refund-badge">
                Возврат
              </span>
            )}
            {!isRefund && entry.type === "prepayment" && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full"
                    style={{ background: "#F5EBD8", color: "#4A3728" }}
                    data-testid="mo-payments-row-type-badge">
                Предоплата
              </span>
            )}
            {!isRefund && entry.type === "payment" && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full"
                    style={{ background: "#E7DFC7", color: "#4A3728" }}
                    data-testid="mo-payments-row-type-badge">
                Оплата
              </span>
            )}
            {!isRefund && entry.type === "postpayment" && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full"
                    style={{ background: "#DAE9DA", color: "#2A6B48" }}
                    data-testid="mo-payments-row-type-badge">
                Доплата
              </span>
            )}
            {isVoided && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full"
                    style={{ background: "#EADFCF", color: "#8B6F4B" }}
                    data-testid="mo-payments-row-voided-badge">
                Аннулировано
              </span>
            )}
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
            {fmtDateTime(entry.paid_at)} · {METHOD_LABEL[entry.method] || entry.method}
          </div>
          {entry.note && (
            <div className="text-[12px] mt-1 leading-snug" style={{ color: "#4A3728" }}>{entry.note}</div>
          )}
          {isVoided && entry.void_reason && (
            <div className="text-[11.5px] mt-1 italic" style={{ color: "#8B6F4B" }}>
              Причина: {entry.void_reason}
            </div>
          )}
        </div>
        {canWrite && !isVoided && (
          <div className="relative shrink-0">
            <button onClick={() => setMenuOpen((v) => !v)}
                    className="w-8 h-8 rounded-full grid place-items-center"
                    style={{ background: "#F7F1E7", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    aria-label="Действия"
                    data-testid="mo-payments-row-menu-btn">
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-9 min-w-[170px] rounded-lg py-1 z-10"
                   style={{ background: "white",
                            boxShadow: "0 4px 16px rgba(35,23,16,0.15), inset 0 0 0 1px #EFE4D0" }}>
                <button onClick={() => { setMenuOpen(false); onEdit(entry); }}
                        className="w-full text-left px-3 py-2 text-[12.5px] flex items-center gap-2 hover:bg-[#F7F1E7]"
                        style={{ color: "#2A1D14" }}
                        data-testid="mo-payments-row-edit">
                  <Pencil size={12} /> Редактировать
                </button>
                <button onClick={() => { setMenuOpen(false); onAnnul(entry); }}
                        className="w-full text-left px-3 py-2 text-[12.5px] flex items-center gap-2 hover:bg-[#F7F1E7]"
                        style={{ color: "#7A2E00" }}
                        data-testid="mo-payments-row-annul">
                  <Ban size={12} /> Аннулировать запись
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------- Add / Edit form ----------------------------------------------- */
function PaymentForm({ mode, initial, currency, orderId, onDone, onCancel }) {
  // mode: "payment" | "refund" | "edit"
  const isEdit = mode === "edit";
  const isRefund = mode === "refund" || (isEdit && initial?.type === "refund");
  const [amount, setAmount] = useState(initial?.amount_minor != null
    ? String(initial.amount_minor / 100).replace(".", ",")
    : "");
  const [paidAt, setPaidAt] = useState(initial?.paid_at
    ? isoToLocalInput(initial.paid_at) : nowLocalInput());
  const [method, setMethod] = useState(initial?.method || "cash");
  // Sprint MO-UX-Simplify: business type of the incoming payment.
  // Default to prepayment for a *new* payment (matches the most common
  // first-money-in flow). Existing rows keep whatever type they had.
  const [payType, setPayType] = useState(
    initial?.type && ["prepayment", "payment", "postpayment"].includes(initial.type)
      ? initial.type : (isRefund ? "payment" : "prepayment")
  );
  const [note, setNote] = useState(initial?.note || "");
  const [busy, setBusy] = useState(false);

  const amountLabel = isRefund ? "Сумма возврата" : "Сумма";
  const primaryLabel = isEdit ? "Сохранить" : (isRefund ? "Добавить возврат" : "Добавить оплату");
  const testidBase = isEdit ? "mo-payments-form-edit" : (isRefund ? "mo-payments-form-refund" : "mo-payments-form-payment");

  const submit = async () => {
    if (busy) return;
    try {
      if (!isEdit) {
        const minor = majorInputToMinor(amount);
        if (minor === "EMPTY" || minor === "INVALID") {
          toast.error("Введите сумму больше нуля");
          return;
        }
        if (!paidAt) { toast.error("Укажите дату и время"); return; }
        setBusy(true);
        const localDate = new Date(paidAt);
        await createOrderPayment({
          order_id: orderId,
          amount_minor: minor,
          currency_code: currency,
          paid_at: localDate.toISOString(),
          method,
          type: isRefund ? "refund" : payType,
          note: note.trim() || undefined,
        });
        toast.success(isRefund ? "Возврат добавлен" : "Оплата добавлена");
      } else {
        setBusy(true);
        const patch = {};
        if (paidAt) patch.paid_at = new Date(paidAt).toISOString();
        if (method) patch.method = method;
        patch.note = note.trim() ? note : null;
        await updateOrderPayment(initial.id, patch);
        toast.success("Изменения сохранены");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg p-3 mb-3 space-y-2.5"
         style={{ background: "#FBF3E4", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid={testidBase}>
      <div className="text-[13px]" style={{ color: "#2A1D14" }}>
        {isEdit ? "Редактировать запись" : (isRefund ? "Новый возврат клиенту" : "Новая оплата")}
      </div>

      {/* Amount (only on create) */}
      {!isEdit && (
        <div>
          <label className="block text-[10.5px] uppercase tracking-[0.14em] mb-1"
                 style={{ color: "#8B6F4B" }}>{amountLabel}</label>
          <div className="flex items-center gap-2">
            <input
              type="text" inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="flex-1 h-9 px-3 rounded-md bg-white text-[13px] outline-none text-right tabular-nums"
              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
              data-testid={`${testidBase}-amount`}
              autoFocus
            />
            <span className="text-[12px] w-10 shrink-0" style={{ color: "#5B4326" }}>{currency}</span>
          </div>
        </div>
      )}

      {isEdit && (
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>
          Сумма и тип записи меняются только через аннулирование и создание новой корректной записи.
        </div>
      )}

      {!isRefund && !isEdit && (
        <div>
          <label className="block text-[10.5px] uppercase tracking-[0.14em] mb-1"
                 style={{ color: "#8B6F4B" }}>Тип платежа</label>
          <div className="flex flex-wrap gap-1.5" data-testid={`${testidBase}-paytype`}>
            {[
              { id: "prepayment",  label: "Предоплата" },
              { id: "payment",     label: "Оплата" },
              { id: "postpayment", label: "Доплата" },
            ].map((t) => (
              <button key={t.id} onClick={() => setPayType(t.id)} type="button"
                      className="h-8 px-3 rounded-xl text-[12px]"
                      style={{
                        background: payType === t.id ? "#4A3728" : "#F7F1E7",
                        color: payType === t.id ? "#F7EFE0" : "#4A3728",
                        boxShadow: payType === t.id ? "none" : "inset 0 0 0 1px #EFE4D0",
                      }}
                      data-testid={`${testidBase}-paytype-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-[10.5px] uppercase tracking-[0.14em] mb-1"
               style={{ color: "#8B6F4B" }}>Дата и время</label>
        <input
          type="datetime-local"
          value={paidAt}
          onChange={(e) => setPaidAt(e.target.value)}
          className="w-full h-9 px-3 rounded-md bg-white text-[13px] outline-none"
          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
          data-testid={`${testidBase}-paid-at`}
        />
      </div>

      <div>
        <label className="block text-[10.5px] uppercase tracking-[0.14em] mb-1"
               style={{ color: "#8B6F4B" }}>Способ оплаты</label>
        <div className="flex flex-wrap gap-1.5" data-testid={`${testidBase}-method`}>
          {METHODS.map((m) => (
            <button key={m} onClick={() => setMethod(m)}
                    className="h-8 px-3 rounded-xl text-[12px]"
                    style={{
                      background: method === m ? "#4A3728" : "#F7F1E7",
                      color: method === m ? "#F7EFE0" : "#4A3728",
                      boxShadow: method === m ? "none" : "inset 0 0 0 1px #EFE4D0",
                    }}
                    data-testid={`${testidBase}-method-${m}`}>
              {METHOD_LABEL[m]}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[10.5px] uppercase tracking-[0.14em] mb-1"
               style={{ color: "#8B6F4B" }}>Комментарий</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          rows={2}
          placeholder="—"
          className="w-full p-2.5 rounded-md bg-white text-[13px] outline-none resize-none"
          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
          data-testid={`${testidBase}-note`}
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel}
                className="h-9 px-3.5 rounded-xl text-[12.5px]"
                style={{ background: "transparent", color: "#4A3728",
                         boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid={`${testidBase}-cancel`}>Отмена</button>
        <button onClick={submit} disabled={busy}
                className="h-9 px-4 rounded-xl text-[12.5px] disabled:opacity-60"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid={`${testidBase}-submit`}>
          {busy ? "…" : primaryLabel}
        </button>
      </div>
    </div>
  );
}

/* -------- Main drawer ---------------------------------------------------- */
export function PaymentsDrawer({ order, canWrite, onClose, onOrderChanged }) {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [pending, setPending] = useState(null); // {mode, initial?}
  const [busy, setBusy] = useState(false);

  const reload = async ({ notifyParent = false } = {}) => {
    try {
      const [s, list] = await Promise.all([
        getOrderPaymentsSummary(order.id),
        listOrderPayments(order.id),
      ]);
      setSummary(s);
      setEntries(list);
      if (notifyParent) onOrderChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [order.id]);

  const currency = order.currency_code;
  const hasPayments = entries.length > 0;

  const askAnnul = async (entry) => {
    if (!canWrite || busy) return;
    if (!window.confirm("Аннулировать эту запись об оплате?")) return;
    const reason = window.prompt("Причина (необязательно):", "") || undefined;
    setBusy(true);
    try {
      await voidOrderPayment(entry.id, reason ? { reason } : {});
      toast.success("Запись аннулирована");
      await reload({ notifyParent: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-payments-drawer">
      <div className="w-full md:max-w-xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5 md:p-6 max-h-[92vh] overflow-y-auto"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)" }}>
        <div className="flex items-start justify-between mb-3">
          <div className="text-[19px]"
               style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            Оплаты по заказу
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "#F7F1E7", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-payments-drawer-close">
            <X size={13} />
          </button>
        </div>

        <DrawerSummary summary={summary} />

        {/* Add form (inline) */}
        {pending && (
          <PaymentForm
            mode={pending.mode}
            initial={pending.initial}
            currency={currency}
            orderId={order.id}
            onDone={async () => { setPending(null); await reload({ notifyParent: true }); }}
            onCancel={() => setPending(null)}
          />
        )}

        {/* History header + add buttons */}
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="text-[10.5px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>
            История оплат
          </div>
          {canWrite && !pending && (
            <div className="flex gap-1.5">
              <button onClick={() => setPending({ mode: "payment" })}
                      className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-payments-add-payment">
                <Plus size={11} strokeWidth={2.5} /> Добавить оплату
              </button>
              {hasPayments && (
                <button onClick={() => setPending({ mode: "refund" })}
                        className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1"
                        style={{ background: "#F7F1E7", color: "#4A3728",
                                 boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                        data-testid="mo-payments-add-refund">
                  <Undo2 size={11} strokeWidth={2.5} /> Добавить возврат
                </button>
              )}
            </div>
          )}
        </div>

        {entries.length === 0 ? (
          <div className="rounded-lg p-4 text-[12.5px]"
               style={{ background: "white", color: "#8B6F4B",
                        boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-payments-empty">
            Оплат пока нет
          </div>
        ) : (
          <div className="space-y-2" data-testid="mo-payments-list">
            {entries.map((e) => (
              <PaymentRow key={e.id} entry={e} currency={currency} canWrite={canWrite}
                          onEdit={(entry) => setPending({ mode: "edit", initial: entry })}
                          onAnnul={askAnnul} />
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-xl text-[13px]"
                  style={{ background: "transparent", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-payments-drawer-done">
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}
